import { useCallback, useState } from 'react';

export interface ConfirmStorage {
  payment_confirm?: boolean;
  price_changed_confirm?: boolean;
}

/**
 * Type guard to validate parsed localStorage data is a valid ConfirmStorage
 *
 * Design Decision: Runtime validation for localStorage data is required because:
 * 1. localStorage data persists across deployments and may have stale schema
 * 2. Users or browser extensions could manually modify localStorage
 * 3. JSON.parse returns `unknown`, requiring explicit type narrowing
 *
 * @see PR Review 2026-01-24: Type Design Issue #3
 */
const isValidConfirmStorage = (data: unknown): data is ConfirmStorage => {
  if (typeof data !== 'object' || data === null) return false;
  const obj = data as Record<string, unknown>;
  if ('payment_confirm' in obj && typeof obj.payment_confirm !== 'boolean')
    return false;
  if (
    'price_changed_confirm' in obj &&
    typeof obj.price_changed_confirm !== 'boolean'
  )
    return false;
  return true;
};

const STORAGE_KEY = 'confirmations';
const EXPIRY_KEY = 'confirmations_expiry';
const EXPIRY_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * 從 localStorage 同步讀取略過狀態（過期或格式錯誤時清除並回傳空物件）
 */
const readFromStorage = (): ConfirmStorage => {
  try {
    const expiryStr = localStorage.getItem(EXPIRY_KEY);
    const expiry = expiryStr ? parseInt(expiryStr, 10) : 0;

    // Check if expired
    if (expiry && Date.now() > expiry) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      return {};
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};

    const parsed: unknown = JSON.parse(stored);
    if (isValidConfirmStorage(parsed)) {
      return parsed;
    }
    // Invalid data, clear storage
    console.warn('[useConfirmStorage] Invalid data in localStorage, clearing');
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch (error) {
    console.error('[useConfirmStorage] Failed to load:', error);
  }
  return {};
};

/**
 * Hook to manage confirmation skip state in localStorage
 * Replaces react-cookie's useCookies with localStorage implementation
 *
 * Features:
 * - Stores "skip for 24 hours" state in localStorage
 * - Auto-expires after 24 hours
 * - Type-safe access to confirmation keys
 *
 * Design Decision:
 * - 初始值以 lazy initializer 同步讀取 localStorage：若改在 useEffect 讀取，
 *   第一次 render 會拿到 `{}`，同一輪的自動開啟 effect 會誤判為未略過而跳出 popup
 * - 寫入時同步寫 localStorage（不放在 setState updater 內），並以 localStorage
 *   為 prev 來源：呼叫後元件立即卸載（例如導頁）時仍能寫入，且多個 hook 實例間不會互相覆蓋
 */
export const useConfirmStorage = () => {
  const [waitingConfirmViewedIn24, setStorageState] =
    useState<ConfirmStorage>(readFromStorage);

  const setWaitingConfirmViewedIn24 = useCallback(
    (updater: (prev: ConfirmStorage) => ConfirmStorage) => {
      const newState = updater(readFromStorage());
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
        // Set expiry if not already set
        if (!localStorage.getItem(EXPIRY_KEY)) {
          localStorage.setItem(EXPIRY_KEY, String(Date.now() + EXPIRY_DURATION));
        }
      } catch (error) {
        console.error('[useConfirmStorage] Failed to save:', error);
      }
      setStorageState(newState);
    },
    []
  );

  return {
    waitingConfirmViewedIn24,
    setWaitingConfirmViewedIn24,
  };
};
